/**
 * Fig. C.1 (degree of dilution) and Fig. D.1 (hazardous distance) of IEC 60079-10-1:2020 as code.
 *
 * Both charts are straight lines on log–log axes, so each line is a power law y = a·x^b over a
 * bounded domain. Digitised on 2026-11-03 from the figures' embedded 300 ppi images (see
 * docs/gate-0.md for the full procedure and numbers). No image or figure is stored here.
 *
 * Provenance
 *   C.1  image 1787×1433 px. Axes calibrated by least squares on all 46 vertical and 37 horizontal
 *        gridlines (max residual 1.43 / 1.48 px; 316.2 px per decade in Qc, 307.4 in uw).
 *        Free fits give slopes 0.9991 (high/medium) and 1.0004 (medium/low); both lines are taken as
 *        slope exactly 1 (MD-012) and only the coefficient is fitted on sub-pixel centrelines:
 *        913 and 1164 samples, rms residual 0.39 % and 0.38 %.
 *        Cross-check (decade gridlines only, per-pixel fit): coefficients within 0.53 % and 0.75 %.
 *   D.1  image 1817×1455 px. Calibrated on all 37 vertical and 19 horizontal gridlines (max residual
 *        1.25 / 1.29 px; 402.4 px per decade in Qc, 624.1 in distance). Free power-law fits on
 *        sub-pixel centrelines: rms residual 0.16 % (diffusive), 0.13 % (heavy gas), 0.18 % (jet).
 *        Cross-check (decade gridlines only, per-pixel fit): predictions within 0.37 % everywhere.
 */

export interface PowerLaw {
  /** y = a · x^b */
  a: number;
  b: number;
}

/** Fig. C.1 axes: Qc 0.001–100 m³/s, uw 0.001–10 m/s. */
export const FIG_C1 = {
  qcRange: [0.001, 100] as const,
  uwRange: [0.001, 10] as const,
  /** Above this line (uw ≥ k·Qc) dilution is high. Represents a flammable volume of 0.1 m³. */
  highMedium: { a: 13.483, b: 1 } satisfies PowerLaw,
  /** Below this line (uw < k·Qc) dilution is low. Represents roughly 100 m³. */
  mediumLow: { a: 0.046559, b: 1 } satisfies PowerLaw,
} as const;

export type ExtentCurve = 'jet' | 'diffusive' | 'heavy';

export interface BoundedCurve extends PowerLaw {
  /** Qc domain of the drawn line, m³/s. Outside it the chart is not to be extrapolated (D.3). */
  qcMin: number;
  qcMax: number;
}

/** Fig. D.1: hazardous distance (m) against Qc (m³/s). */
export const FIG_D1: Record<ExtentCurve, BoundedCurve> = {
  diffusive: { a: 4.28343, b: 0.50163, qcMin: 0.055, qcMax: 31.9 },
  heavy: { a: 9.06743, b: 0.50277, qcMin: 0.0272, qcMax: 28.3 },
  jet: { a: 2.15523, b: 0.52338, qcMin: 0.2306, qcMax: 31.9 },
};

export const evaluate = (c: PowerLaw, x: number): number => c.a * Math.pow(x, c.b);
